import { createSelector } from "reselect"
import { first, get, map } from "lodash"
import { getCurrencyScaleForValues, makeScaledFormatterSelector, type ValueFormatter } from "helpers/formatters/currency-scale"
import { buildScaledCurrencyLabel } from "helpers/metrics/currency-scale-label"
import { METRIC_TYPES } from "helpers/assets/constants"
import { getContributorsGrouping, getSelectedField, getSelectedGroup, getSelectedGroupSiblings, getSelectedGroupValue, getSortedContributors, getSortedGroupContributors } from "./index"
import { getGroupsDropdownColumns } from "./group"

interface ScaleProps {
  metricType?: string
  currency?: string | null
  selectedValue?: number
  details?: readonly { id?: string, value?: unknown, favorite?: boolean }[]
  formatter?: ValueFormatter
  contributorsGrouping?: { id?: string, label?: string }
  selectedGroup?: { id?: string } | null
}

type RowValuesSelector = (state: unknown, props: ScaleProps) => unknown[]

const getMetricType = (_: unknown, props: ScaleProps) => props.metricType
const getCurrency = (_: unknown, props: ScaleProps) => props.currency
const getSelectedValue = (_: unknown, props: ScaleProps) => props.selectedValue
const getDetails = (_: unknown, props: ScaleProps) => props.details

const isCurrencyExposure = (metricType: string | undefined) => metricType === METRIC_TYPES.CURRENCY_EXPOSURE

const getHeaderRowValue = createSelector(
  [getSelectedGroup, getSelectedGroupValue, getSelectedValue],
  (selectedGroup, selectedGroupValue, selectedValue) => selectedGroup ? selectedGroupValue : selectedValue,
)

const getFlatRowValues: RowValuesSelector = createSelector(
  [getSortedContributors, getSelectedField],
  (contributors, selectedField) => map(contributors, row => get(row, selectedField)),
)

const getGroupedRowValues: RowValuesSelector = createSelector(
  [getSortedGroupContributors, getSelectedGroup, getSelectedField],
  (groupedContributors, selectedGroup, selectedField) => {
    const rows = selectedGroup ? first(groupedContributors)?.contributors : groupedContributors
    return map(rows, row => get(row, selectedField))
  },
)

const getTableRowValues: RowValuesSelector = (state, props) => getContributorsGrouping(state, props)
  ? getGroupedRowValues(state, props)
  : getFlatRowValues(state, props)

const getDropdownValues = createSelector(
  [getSelectedGroup, getSelectedGroupSiblings, getDetails],
  (selectedGroup, selectedGroupSiblings, details) => map(selectedGroup ? selectedGroupSiblings : details, "value"),
)

export const getTableCurrencyScale = createSelector(
  [getTableRowValues, getHeaderRowValue, getMetricType],
  (rowValues, headerRowValue, metricType): string | null => isCurrencyExposure(metricType)
    ? getCurrencyScaleForValues([...rowValues, headerRowValue])
    : null,
)

export const getTableFormatter = makeScaledFormatterSelector(getTableCurrencyScale)

export const getDropdownCurrencyScale = createSelector(
  [getDropdownValues, getMetricType],
  (values, metricType): string | null => isCurrencyExposure(metricType) ? getCurrencyScaleForValues(values) : null,
)

export const getDropdownFormatter = makeScaledFormatterSelector(getDropdownCurrencyScale)

export const getScaledGroupsDropdownColumns = createSelector(
  [getGroupsDropdownColumns, getDropdownCurrencyScale, getCurrency],
  (columns, currencyScale, currency) => currencyScale
    ? map(columns, column => column.id === "value"
      ? { ...column, label: buildScaledCurrencyLabel(column.label, currencyScale, currency) }
      : column)
    : columns,
)
